import React, { useState, useEffect, useRef } from "react";
import {
	Download,
	CheckCircle,
	AlertCircle,
	Clock,
	Database,
	Cpu,
	Upload,
} from "lucide-react";

const ExportModal = ({
	isOpen,
	onClose,
	onExport,
	isExporting,
	title = "Export VFF Data",
	description = "Export your filtered VFF data to Excel format (.xlsx). The file will be prepared and stored securely, then you'll receive a download link.",
}) => {
	const [exportResult, setExportResult] = useState(null);
	const [exportError, setExportError] = useState(null);
	const [progress, setProgress] = useState({
		stage: "starting",
		percentage: 0,
		currentStep: "Initializing export...",
		steps: {
			query: {
				completed: false,
				label: "Executing database query",
				icon: Database,
			},
			processing: { completed: false, label: "Processing data", icon: Cpu },
			upload: { completed: false, label: "Uploading to storage", icon: Upload },
		},
	});
	const progressIntervalRef = useRef(null);
	const simulateProgress = () => {
		let currentProgress = 0;
		const stages = [
			{
				stage: "query",
				step: "Executing database query...",
				duration: 5000,
				percentage: 25,
			},
			{
				stage: "processing",
				step: "Processing and mapping data...",
				duration: 3000,
				percentage: 55,
			},
			{
				stage: "upload",
				step: "Uploading to secure storage...",
				duration: 8000,
				percentage: 95,
			},
		];

		let stageIndex = 0;
		let stageStartTime = Date.now();

		progressIntervalRef.current = setInterval(() => {
			const currentStage = stages[stageIndex];

			if (!currentStage) return;

			// Calculate progress within current stage
			const stageElapsed = Date.now() - stageStartTime;
			const stageProgress = Math.min(stageElapsed / currentStage.duration, 1);
			const previousProgress =
				stageIndex > 0 ? stages[stageIndex - 1].percentage : 0;
			const stageRange = currentStage.percentage - previousProgress;
			currentProgress = previousProgress + stageProgress * stageRange;

			setProgress((prev) => ({
				...prev,
				stage: currentStage.stage,
				percentage: Math.round(currentProgress),
				currentStep: currentStage.step,
				steps: {
					...prev.steps,
					[currentStage.stage]: {
						...prev.steps[currentStage.stage],
						completed: stageProgress >= 1,
					},
				},
			}));

			// Move to next stage
			if (stageProgress >= 1 && stageIndex < stages.length - 1) {
				// Mark current stage as completed
				setProgress((prev) => ({
					...prev,
					steps: {
						...prev.steps,
						[currentStage.stage]: {
							...prev.steps[currentStage.stage],
							completed: true,
						},
					},
				}));

				stageIndex++;
				stageStartTime = Date.now();
			}

			// Complete when export finishes or when we reach the end
			if (!isExporting && currentProgress > 0) {
				setProgress((prev) => ({
					...prev,
					percentage: 100,
					currentStep: "Export completed successfully!",
					steps: {
						query: { ...prev.steps.query, completed: true },
						processing: { ...prev.steps.processing, completed: true },
						upload: { ...prev.steps.upload, completed: true },
					},
				}));
				clearInterval(progressIntervalRef.current);
			}
		}, 300); // Update every 300ms for smoother animation
	};

	useEffect(() => {
		if (isExporting) {
			setProgress({
				stage: "starting",
				percentage: 0,
				currentStep: "Initializing export...",
				steps: {
					query: {
						completed: false,
						label: "Executing database query",
						icon: Database,
					},
					processing: { completed: false, label: "Processing data", icon: Cpu },
					upload: {
						completed: false,
						label: "Uploading to storage",
						icon: Upload,
					},
				},
			});
			simulateProgress();
		} else {
			if (progressIntervalRef.current) {
				clearInterval(progressIntervalRef.current);
			}
		}

		return () => {
			if (progressIntervalRef.current) {
				clearInterval(progressIntervalRef.current);
			}
		};
	}, [isExporting]);

	const handleExport = async () => {
		try {
			setExportError(null);
			setExportResult(null);

			const result = await onExport();
			setExportResult(result);
		} catch (error) {
			setExportError(error.message);
		}
	};

	const handleDownload = () => {
		if (exportResult?.download_url) {
			window.open(exportResult.download_url, "_blank");
		}
	};
	const handleClose = () => {
		setExportResult(null);
		setExportError(null);
		setProgress({
			stage: "starting",
			percentage: 0,
			currentStep: "Initializing export...",
			steps: {
				query: {
					completed: false,
					label: "Executing database query",
					icon: Database,
				},
				processing: { completed: false, label: "Processing data", icon: Cpu },
				upload: {
					completed: false,
					label: "Uploading to storage",
					icon: Upload,
				},
			},
		});
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
		}
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: "rgba(0, 0, 0, 0.5)",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				zIndex: 10000,
			}}
		>
			<div
				style={{
					backgroundColor: "white",
					borderRadius: "12px",
					padding: "24px",
					minWidth: "400px",
					maxWidth: "600px",
					boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
				}}
			>
				{" "}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						marginBottom: "20px",
					}}
				>
					<Download
						size={24}
						style={{ marginRight: "12px", color: "#1e2832" }}
					/>{" "}
					<h2
						style={{
							margin: 0,
							color: "#1e2832",
							fontSize: "20px",
							fontFamily: "MAN Europe_Bold",
						}}
					>
						{title}
					</h2>
				</div>
				{!isExporting && !exportResult && !exportError && (
					<div>
						{" "}
						<p
							style={{
								color: "#666",
								marginBottom: "20px",
								fontFamily: "MAN Europe_Bold",
							}}
						>
							{description}
						</p>
						<div
							style={{
								display: "flex",
								gap: "12px",
								justifyContent: "flex-end",
							}}
						>
							<button
								onClick={handleClose}
								style={{
									padding: "10px 20px",
									border: "1px solid #ddd",
									backgroundColor: "white",
									color: "#666",
									borderRadius: "6px",
									cursor: "pointer",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								Cancel
							</button>
							<button
								onClick={handleExport}
								style={{
									padding: "10px 20px",
									border: "none",
									backgroundColor: "#1e2832",
									color: "white",
									borderRadius: "6px",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									gap: "8px",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								<Download size={16} />
								Start Export
							</button>
						</div>
					</div>
				)}{" "}
				{isExporting && (
					<div style={{ textAlign: "center", padding: "20px 0" }}>
						<div
							style={{
								width: "40px",
								height: "40px",
								border: "4px solid #f3f3f3",
								borderTop: "4px solid #1e2832",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								margin: "0 auto 16px",
							}}
						/>
						<h3
							style={{
								color: "#1e2832",
								margin: "0 0 8px 0",
								fontFamily: "MAN Europe_Bold",
							}}
						>
							Preparing Your Export
						</h3>
						<p
							style={{
								color: "#666",
								margin: "0 0 20px 0",
								fontFamily: "MAN Europe_Bold",
							}}
						>
							{progress.currentStep}
						</p>

						{/* Progress Bar */}
						<div style={{ marginBottom: "24px" }}>
							<div
								style={{
									width: "100%",
									height: "8px",
									backgroundColor: "#f3f3f3",
									borderRadius: "4px",
									overflow: "hidden",
									marginBottom: "8px",
								}}
							>
								<div
									style={{
										width: `${progress.percentage}%`,
										height: "100%",
										backgroundColor: "#1e2832",
										borderRadius: "4px",
										transition: "width 0.3s ease-in-out",
									}}
								/>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									fontSize: "14px",
									color: "#666",
								}}
							>
								<span style={{ fontFamily: "MAN Europe_Bold" }}>Progress</span>
								<span
									style={{ fontFamily: "MAN Europe_Bold", color: "#1e2832" }}
								>
									{progress.percentage}%
								</span>
							</div>
						</div>

						{/* Step Indicators */}
						<div style={{ textAlign: "left", marginTop: "16px" }}>
							{Object.entries(progress.steps).map(([key, step]) => {
								const IconComponent = step.icon;
								const isActive = progress.stage === key;
								const isCompleted = step.completed;

								return (
									<div
										key={key}
										style={{
											display: "flex",
											alignItems: "center",
											marginBottom: "12px",
											padding: "8px 12px",
											borderRadius: "6px",
											backgroundColor: isActive ? "#f8f9fa" : "transparent",
											border: isActive
												? "1px solid #1e2832"
												: "1px solid transparent",
										}}
									>
										<div
											style={{
												width: "24px",
												height: "24px",
												borderRadius: "50%",
												backgroundColor: isCompleted
													? "#6b9700"
													: isActive
														? "#1e2832"
														: "#e5e5e5",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												marginRight: "12px",
												transition: "all 0.3s ease",
											}}
										>
											{isCompleted ? (
												<CheckCircle size={14} style={{ color: "white" }} />
											) : (
												<IconComponent
													size={14}
													style={{ color: isActive ? "white" : "#999" }}
												/>
											)}
										</div>
										<span
											style={{
												fontFamily: "MAN Europe_Bold",
												color: isCompleted
													? "#6b9700"
													: isActive
														? "#1e2832"
														: "#666",
												fontSize: "14px",
											}}
										>
											{step.label}
										</span>
										{isActive && !isCompleted && (
											<div
												style={{
													marginLeft: "auto",
													width: "16px",
													height: "16px",
													border: "2px solid #f3f3f3",
													borderTop: "2px solid #1e2832",
													borderRadius: "50%",
													animation: "spin 0.8s linear infinite",
												}}
											/>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}
				{exportResult && (
					<div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginBottom: "16px",
								padding: "12px",
								borderRadius: "8px",
								border: "1px solid black",
							}}
						>
							<CheckCircle
								size={20}
								style={{ color: "#cc0000", marginRight: "12px" }}
							/>
							<div>
								<h4
									style={{
										margin: "0 0 4px 0",
										color: "black",
										fontFamily: "MAN Europe_Bold",
									}}
								>
									Export Completed Successfully!
								</h4>
								<p
									style={{
										margin: 0,
										fontSize: "14px",
										color: "#1e2832",
										fontFamily: "MAN Europe_Bold",
									}}
								>
									{exportResult.record_count?.toLocaleString()} records exported
								</p>
							</div>
						</div>

						<div style={{ marginBottom: "20px" }}>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "8px",
								}}
							>
								<span style={{ color: "#666", fontFamily: "MAN Europe_Bold" }}>
									File:
								</span>
								<span style={{ fontFamily: "MAN Europe_Bold" }}>
									{exportResult.filename}
								</span>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "8px",
								}}
							>
								<span style={{ color: "#666", fontFamily: "MAN Europe_Bold" }}>
									Processing Time:
								</span>
								<span style={{ fontFamily: "MAN Europe_Bold" }}>
									{exportResult.performance?.total_time_seconds}s
								</span>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									marginBottom: "8px",
								}}
							>
								<span style={{ color: "#666", fontFamily: "MAN Europe_Bold" }}>
									Link Expires:
								</span>
								<span
									style={{
										display: "flex",
										alignItems: "center",
										gap: "4px",
										fontFamily: "MAN Europe_Bold",
									}}
								>
									<Clock size={14} />
									{Math.floor(exportResult.expires_in / 60)} minutes
								</span>
							</div>
						</div>

						<div
							style={{
								display: "flex",
								gap: "12px",
								justifyContent: "flex-end",
							}}
						>
							<button
								onClick={handleClose}
								style={{
									padding: "10px 20px",
									border: "1px solid #ddd",
									backgroundColor: "white",
									color: "#666",
									borderRadius: "6px",
									cursor: "pointer",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								Close
							</button>
							<button
								onClick={handleDownload}
								style={{
									padding: "10px 20px",
									border: "none",
									backgroundColor: "#6b9700",
									color: "white",
									borderRadius: "6px",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									gap: "8px",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								<Download size={16} />
								Download File
							</button>
						</div>
					</div>
				)}
				{exportError && (
					<div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginBottom: "16px",
								padding: "12px",
								backgroundColor: "#fef2f2",
								borderRadius: "8px",
								border: "1px solid #ef4444",
							}}
						>
							<AlertCircle
								size={20}
								style={{ color: "#ef4444", marginRight: "12px" }}
							/>
							<div>
								<h4 style={{ margin: "0 0 4px 0", color: "#ef4444" }}>
									Export Failed
								</h4>
								<p style={{ margin: 0, fontSize: "14px", color: "#dc2626" }}>
									{exportError}
								</p>
							</div>
						</div>

						<div
							style={{
								display: "flex",
								gap: "12px",
								justifyContent: "flex-end",
							}}
						>
							<button
								onClick={handleClose}
								style={{
									padding: "10px 20px",
									border: "1px solid #ddd",
									backgroundColor: "white",
									color: "#666",
									borderRadius: "6px",
									cursor: "pointer",
								}}
							>
								Close
							</button>
							<button
								onClick={handleExport}
								style={{
									padding: "10px 20px",
									border: "none",
									backgroundColor: "#1e2832",
									color: "white",
									borderRadius: "6px",
									cursor: "pointer",
								}}
							>
								Try Again
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ExportModal;
